<template>
  <div class="post-comments">
    <div
      v-if="loading"
      class="comments-spinner"
    >
      <b-spinner
        small
        variant="secondary"
      />
    </div>

    <template v-else>
      <div
        v-if="comments.length > 0"
        class="comments-list"
      >
        <div
          v-for="comment in comments"
          :key="comment.id"
          class="comment-row"
        >
          <div
            v-if="comment.is_deleted"
            class="comment-deleted"
          >
            <i class="fa fa-ban mr-1" />
            Comentario eliminado
          </div>

          <template v-else>
            <b-avatar
              size="26"
              variant="primary"
              :text="getInitials(comment.author_name)"
              class="comment-avatar"
            />
            <div class="comment-bubble">
              <div class="comment-author">
                {{ comment.author_name }}
              </div>

              <div
                v-if="editingId === comment.id"
                class="edit-form"
              >
                <textarea
                  ref="editTextarea"
                  v-model="editContent"
                  class="edit-textarea"
                  rows="2"
                  @keydown="onEditKeydown($event, comment)"
                  @input="onEditInput"
                  @blur="onEditBlur"
                />

                <div
                  v-if="showEditMentionDropdown"
                  class="mention-dropdown mention-dropdown--edit"
                >
                  <div
                    v-if="editMentionLoading"
                    class="mention-loading-row"
                  >
                    <b-spinner
                      small
                      variant="secondary"
                    />
                  </div>
                  <template v-else>
                    <button
                      v-for="(user, idx) in editMentionResults"
                      :key="user.id"
                      :class="['mention-item', { 'mention-item--active': editMentionHighlightIndex === idx }]"
                      @mousedown.prevent="selectEditMention(user)"
                    >
                      <b-avatar
                        size="18"
                        variant="primary"
                        :text="user.initials"
                        class="mention-item-avatar"
                      />
                      <span class="mention-item-name">{{ user.fullName }}</span>
                    </button>
                    <div
                      v-if="editMentionResults.length === 0"
                      class="mention-empty"
                    >
                      Sin resultados
                    </div>
                  </template>
                </div>

                <div class="edit-actions">
                  <button
                    class="btn-link-save"
                    :disabled="savingEdit || !editContent.trim()"
                    @click="confirmEdit(comment)"
                  >
                    <b-spinner
                      v-if="savingEdit"
                      small
                    />
                    <span v-else>Guardar</span>
                  </button>
                  <span class="dot-sep">·</span>
                  <button
                    class="btn-link-cancel"
                    @click="cancelEdit"
                  >
                    Cancelar
                  </button>
                </div>
              </div>

              <!-- eslint-disable-next-line vue/no-v-html -->
              <p
                v-else
                class="comment-text"
                v-html="renderMentions(comment.content)"
              />

              <div
                v-if="editingId !== comment.id"
                class="comment-footer"
              >
                <span class="comment-time">{{ formatTime(comment.created_at) }}</span>
                <span
                  v-if="comment.edited_at"
                  class="comment-edited"
                >· editado</span>
                <template v-if="canManage(comment)">
                  <span class="dot-sep">·</span>
                  <button
                    class="btn-comment-action"
                    @click="startEdit(comment)"
                  >
                    Editar
                  </button>
                  <span class="dot-sep">·</span>
                  <button
                    class="btn-comment-action danger"
                    @click="handleDelete(comment)"
                  >
                    Eliminar
                  </button>
                </template>
              </div>
            </div>
          </template>
        </div>

        <div
          v-if="loadingMore"
          class="comments-spinner mt-2"
        >
          <b-spinner
            small
            variant="secondary"
          />
        </div>

        <button
          v-if="hasMore && !loadingMore"
          class="load-more-btn"
          @click="loadMore"
        >
          Ver más comentarios
        </button>
      </div>

      <p
        v-else
        class="no-comments"
      >
        Sé el primero en comentar
      </p>

      <div class="new-comment-row">
        <b-avatar
          size="26"
          variant="primary"
          :text="currentUserInitials"
          class="flex-shrink-0"
        />
        <div class="input-wrapper">
          <div class="mention-wrapper">
            <textarea
              ref="commentInput"
              v-model="newCommentText"
              class="comment-input"
              placeholder="Escribe un comentario..."
              rows="1"
              @keydown="onNewCommentKeydown"
              @input="onNewCommentInput"
              @blur="onNewCommentBlur"
            />
            <div
              v-if="showMentionDropdown"
              class="mention-dropdown mention-dropdown--new"
            >
              <div
                v-if="mentionLoading"
                class="mention-loading-row"
              >
                <b-spinner
                  small
                  variant="secondary"
                />
              </div>
              <template v-else>
                <button
                  v-for="(user, idx) in mentionResults"
                  :key="user.id"
                  :class="['mention-item', { 'mention-item--active': mentionHighlightIndex === idx }]"
                  @mousedown.prevent="selectMention(user)"
                >
                  <b-avatar
                    size="18"
                    variant="primary"
                    :text="user.initials"
                    class="mention-item-avatar"
                  />
                  <span class="mention-item-name">{{ user.fullName }}</span>
                </button>
                <div
                  v-if="mentionResults.length === 0"
                  class="mention-empty"
                >
                  Sin resultados
                </div>
              </template>
            </div>
          </div>
          <button
            class="send-btn"
            :disabled="!newCommentText.trim() || submitting"
            @click="submitComment"
          >
            <b-spinner
              v-if="submitting"
              small
            />
            <i
              v-else
              class="fa fa-paper-plane"
            />
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { ref, computed, onMounted } from '@vue/composition-api'
import { getPostComments, createComment, updateComment, deleteComment, getMentionableUsers } from '../Services'
import MentionableUser from '../models/MentionableUser'

const MENTION_DEBOUNCE_MS = 250

const escapeHtml = (str) => str
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;')

export default {
  name: 'PostComments',
  props: {
    postId: {
      type: Number,
      required: true
    }
  },
  setup (props, context) {
    const comments = ref([])
    const loading = ref(true)
    const loadingMore = ref(false)
    const hasMore = ref(false)
    const currentPage = ref(1)
    const newCommentText = ref('')
    const submitting = ref(false)
    const editingId = ref(null)
    const editContent = ref('')
    const savingEdit = ref(false)

    // --- Mention state: new comment ---
    const mentionQuery = ref(null)
    const mentionStartIndex = ref(0)
    const mentionResults = ref([])
    const mentionLoading = ref(false)
    const mentionHighlightIndex = ref(0)
    const newCommentMentionedIds = ref([])
    let mentionDebounceTimer = null

    // --- Mention state: edit ---
    const editMentionQuery = ref(null)
    const editMentionStartIndex = ref(0)
    const editMentionResults = ref([])
    const editMentionLoading = ref(false)
    const editMentionHighlightIndex = ref(0)
    const editMentionedIds = ref(null)
    let editMentionDebounceTimer = null

    const showMentionDropdown = computed(() => mentionQuery.value !== null)
    const showEditMentionDropdown = computed(() => editMentionQuery.value !== null)

    const userInfo = computed(() => context.root.$store.getters.userInfo || {})
    const rootUsers = computed(() => context.root.$store.getters.rootUsers || [])

    const currentUserInitials = computed(() => {
      const name = userInfo.value.name || 'U'
      return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
    })

    const getInitials = (name = '') =>
      name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()

    const formatTime = (ts) => context.root.$moment(ts).fromNow()

    const canManage = (comment) => {
      const uid = userInfo.value.id
      const email = userInfo.value.email
      const permissions = userInfo.value.permissions || []
      return comment.user_id === uid ||
        rootUsers.value.includes(email) ||
        permissions.includes('DELETE_ANY_NEWS_FEED_POST')
    }

    const renderMentions = (text) => {
      if (!text) return ''
      return escapeHtml(text).replace(/@(\S+)/g, '<span class="mention-tag">@$1</span>')
    }

    // ── Mention detection ────────────────────────────────────────────────────

    const detectMentionAtCursor = (text, cursorPos) => {
      const textBefore = text.substring(0, cursorPos)
      // Match @word at the end of the text before cursor (supports accented chars)
      const match = textBefore.match(/@([\w\u00C0-\u017F]*)$/)
      if (!match) return null
      return {
        query: match[1],
        startIndex: cursorPos - match[0].length
      }
    }

    const fetchMentions = async (query, isEdit) => {
      try {
        if (isEdit) editMentionLoading.value = true
        else mentionLoading.value = true

        const raw = await getMentionableUsers(query)
        const results = raw.map(u => new MentionableUser(u))

        if (isEdit) {
          editMentionResults.value = results
          editMentionHighlightIndex.value = 0
        } else {
          mentionResults.value = results
          mentionHighlightIndex.value = 0
        }
      } catch {
        // silent
      } finally {
        if (isEdit) editMentionLoading.value = false
        else mentionLoading.value = false
      }
    }

    const scheduleFetch = (query, isEdit) => {
      if (isEdit) {
        clearTimeout(editMentionDebounceTimer)
        editMentionDebounceTimer = setTimeout(() => fetchMentions(query, true), MENTION_DEBOUNCE_MS)
      } else {
        clearTimeout(mentionDebounceTimer)
        mentionDebounceTimer = setTimeout(() => fetchMentions(query, false), MENTION_DEBOUNCE_MS)
      }
    }

    const closeMentionDropdown = () => {
      mentionQuery.value = null
      mentionResults.value = []
    }

    const closeEditMentionDropdown = () => {
      editMentionQuery.value = null
      editMentionResults.value = []
    }

    // ── New comment handlers ──────────────────────────────────────────────────

    const onNewCommentInput = (event) => {
      const el = event.target
      // Auto-resize
      el.style.height = 'auto'
      el.style.height = el.scrollHeight + 'px'

      const detected = detectMentionAtCursor(el.value, el.selectionStart)
      if (detected) {
        mentionQuery.value = detected.query
        mentionStartIndex.value = detected.startIndex
        scheduleFetch(detected.query, false)
      } else {
        closeMentionDropdown()
      }
    }

    const onNewCommentBlur = () => {
      setTimeout(closeMentionDropdown, 150)
    }

    const selectMention = (user) => {
      const insertText = `@${user.name} `
      const before = newCommentText.value.substring(0, mentionStartIndex.value)
      const after = newCommentText.value.substring(mentionStartIndex.value + 1 + mentionQuery.value.length)
      newCommentText.value = before + insertText + after

      if (!newCommentMentionedIds.value.includes(user.id)) {
        newCommentMentionedIds.value = [...newCommentMentionedIds.value, user.id]
      }

      closeMentionDropdown()

      context.root.$nextTick(() => {
        const el = context.root.$refs.commentInput
        if (el) {
          el.focus()
          const pos = before.length + insertText.length
          el.setSelectionRange(pos, pos)
          el.style.height = 'auto'
          el.style.height = el.scrollHeight + 'px'
        }
      })
    }

    const onNewCommentKeydown = (event) => {
      if (showMentionDropdown.value) {
        if (event.key === 'ArrowDown') {
          event.preventDefault()
          mentionHighlightIndex.value = Math.min(mentionHighlightIndex.value + 1, mentionResults.value.length - 1)
        } else if (event.key === 'ArrowUp') {
          event.preventDefault()
          mentionHighlightIndex.value = Math.max(mentionHighlightIndex.value - 1, 0)
        } else if (event.key === 'Enter' || event.key === 'Tab') {
          event.preventDefault()
          const user = mentionResults.value[mentionHighlightIndex.value]
          if (user) selectMention(user)
        } else if (event.key === 'Escape') {
          event.preventDefault()
          closeMentionDropdown()
        }
      } else if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        submitComment()
      }
    }

    // ── Edit handlers ─────────────────────────────────────────────────────────

    const onEditInput = (event) => {
      const el = event.target
      const detected = detectMentionAtCursor(el.value, el.selectionStart)
      if (detected) {
        editMentionQuery.value = detected.query
        editMentionStartIndex.value = detected.startIndex
        scheduleFetch(detected.query, true)
      } else {
        closeEditMentionDropdown()
      }
    }

    const onEditBlur = () => {
      setTimeout(closeEditMentionDropdown, 150)
    }

    const selectEditMention = (user) => {
      const insertText = `@${user.name} `
      const before = editContent.value.substring(0, editMentionStartIndex.value)
      const after = editContent.value.substring(editMentionStartIndex.value + 1 + editMentionQuery.value.length)
      editContent.value = before + insertText + after

      if (editMentionedIds.value === null) editMentionedIds.value = []
      if (!editMentionedIds.value.includes(user.id)) {
        editMentionedIds.value = [...editMentionedIds.value, user.id]
      }

      closeEditMentionDropdown()

      context.root.$nextTick(() => {
        const ref = context.root.$refs.editTextarea
        const el = Array.isArray(ref) ? ref[0] : ref
        if (el) {
          el.focus()
          const pos = before.length + insertText.length
          el.setSelectionRange(pos, pos)
        }
      })
    }

    const onEditKeydown = (event, comment) => {
      if (showEditMentionDropdown.value) {
        if (event.key === 'ArrowDown') {
          event.preventDefault()
          editMentionHighlightIndex.value = Math.min(editMentionHighlightIndex.value + 1, editMentionResults.value.length - 1)
        } else if (event.key === 'ArrowUp') {
          event.preventDefault()
          editMentionHighlightIndex.value = Math.max(editMentionHighlightIndex.value - 1, 0)
        } else if (event.key === 'Enter' || event.key === 'Tab') {
          event.preventDefault()
          const user = editMentionResults.value[editMentionHighlightIndex.value]
          if (user) selectEditMention(user)
        } else if (event.key === 'Escape') {
          event.preventDefault()
          closeEditMentionDropdown()
        }
      } else if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        confirmEdit(comment)
      } else if (event.key === 'Escape') {
        cancelEdit()
      }
    }

    // ── API actions ───────────────────────────────────────────────────────────

    const fetchPage = async (page) => {
      const result = await getPostComments(props.postId, page)
      if (result.length > 0) {
        const total = result[0].total
        hasMore.value = comments.value.length + result.length < total
        currentPage.value = page
      } else {
        hasMore.value = false
      }
      return result
    }

    const loadMore = async () => {
      loadingMore.value = true
      try {
        const result = await fetchPage(currentPage.value + 1)
        comments.value = [...comments.value, ...result]
      } finally {
        loadingMore.value = false
      }
    }

    const submitComment = async () => {
      const text = newCommentText.value.trim()
      if (!text || submitting.value) return
      submitting.value = true
      try {
        const created = await createComment(props.postId, text, newCommentMentionedIds.value)
        comments.value.push({
          ...created,
          author_name: userInfo.value.name || '',
          is_deleted: false
        })
        newCommentText.value = ''
        newCommentMentionedIds.value = []
        context.root.$nextTick(() => {
          const el = context.root.$refs.commentInput
          if (el) el.style.height = 'auto'
        })
      } finally {
        submitting.value = false
      }
    }

    const startEdit = (comment) => {
      editingId.value = comment.id
      editContent.value = comment.content
      editMentionedIds.value = null
      closeEditMentionDropdown()
    }

    const cancelEdit = () => {
      editingId.value = null
      editContent.value = ''
      editMentionedIds.value = null
      closeEditMentionDropdown()
    }

    const confirmEdit = async (comment) => {
      const text = editContent.value.trim()
      if (!text || savingEdit.value) return
      savingEdit.value = true
      try {
        await updateComment(comment.id, text, editMentionedIds.value !== null ? editMentionedIds.value : undefined)
        const target = comments.value.find(c => c.id === comment.id)
        if (target) {
          target.content = text
          target.edited_at = new Date().toISOString()
        }
        cancelEdit()
      } catch {
        cancelEdit()
      } finally {
        savingEdit.value = false
      }
    }

    const handleDelete = async (comment) => {
      try {
        await deleteComment(comment.id)
        const target = comments.value.find(c => c.id === comment.id)
        if (target) {
          target.is_deleted = true
          target.content = null
        }
      } catch {
        // silent
      }
    }

    onMounted(async () => {
      try {
        comments.value = await fetchPage(1)
      } catch {
        // silently show empty state on error
      } finally {
        loading.value = false
      }
    })

    return {
      comments,
      loading,
      loadingMore,
      hasMore,
      newCommentText,
      submitting,
      editingId,
      editContent,
      savingEdit,
      showMentionDropdown,
      showEditMentionDropdown,
      mentionResults,
      mentionLoading,
      mentionHighlightIndex,
      editMentionResults,
      editMentionLoading,
      editMentionHighlightIndex,
      currentUserInitials,
      getInitials,
      formatTime,
      canManage,
      renderMentions,
      loadMore,
      submitComment,
      startEdit,
      cancelEdit,
      confirmEdit,
      handleDelete,
      onNewCommentInput,
      onNewCommentKeydown,
      onNewCommentBlur,
      selectMention,
      onEditInput,
      onEditKeydown,
      onEditBlur,
      selectEditMention
    }
  }
}
</script>

<style scoped>
.post-comments {
  border-top: 1px solid #f0f4f8;
  padding-top: 0.75rem;
  margin-top: 0.75rem;
}

.comments-spinner {
  display: flex;
  justify-content: center;
  padding: 0.5rem 0;
}

.comments-list {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-bottom: 0.75rem;
  max-height: 380px;
  overflow-y: auto;
  padding-right: 2px;
}

.comment-row {
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
}

.comment-deleted {
  font-size: 0.72rem;
  color: #a0aec0;
  font-style: italic;
  padding: 0.3rem 0.6rem;
  background: #f8fafc;
  border-radius: 6px;
  width: 100%;
}

.comment-avatar {
  flex-shrink: 0;
  margin-top: 2px;
}

.comment-bubble {
  flex: 1;
  background: #f7fafc;
  border-radius: 10px;
  padding: 0.45rem 0.7rem 0.35rem;
  min-width: 0;
}

.comment-author {
  font-size: 0.72rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 0.1rem;
}

.comment-text {
  font-size: 0.78rem;
  color: #4a5568;
  margin: 0;
  line-height: 1.45;
  word-break: break-word;
}

.comment-footer {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.2rem;
  margin-top: 0.25rem;
}

.comment-time,
.comment-edited {
  font-size: 0.67rem;
  color: #a0aec0;
}

.dot-sep {
  font-size: 0.67rem;
  color: #cbd5e0;
}

.btn-comment-action {
  background: none;
  border: none;
  padding: 0;
  font-size: 0.67rem;
  font-weight: 500;
  color: #718096;
  cursor: pointer;
  line-height: 1;
}

.btn-comment-action:hover { color: #2d3748; }
.btn-comment-action.danger:hover { color: #e53e3e; }

/* Inline edit */
.edit-form {
  position: relative;
}

.edit-textarea {
  width: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0.3rem 0.5rem;
  font-size: 0.78rem;
  color: #2d3748;
  resize: none;
  outline: none;
  background: white;
}

.edit-textarea:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.12);
}

.edit-actions {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  margin-top: 0.3rem;
}

.btn-link-save,
.btn-link-cancel {
  background: none;
  border: none;
  padding: 0;
  font-size: 0.72rem;
  font-weight: 600;
  cursor: pointer;
  line-height: 1;
}

.btn-link-save { color: #667eea; }
.btn-link-save:disabled { opacity: 0.4; }
.btn-link-cancel { color: #718096; }

/* Load more */
.load-more-btn {
  background: none;
  border: none;
  font-size: 0.72rem;
  font-weight: 600;
  color: #667eea;
  cursor: pointer;
  padding: 0.25rem 0;
  display: block;
}

.load-more-btn:hover { text-decoration: underline; }

.no-comments {
  font-size: 0.75rem;
  color: #a0aec0;
  text-align: center;
  margin: 0 0 0.75rem;
}

/* New comment input */
.new-comment-row {
  display: flex;
  gap: 0.5rem;
  align-items: flex-end;
  border-top: 1px solid #f0f4f8;
  padding-top: 0.6rem;
}

.input-wrapper {
  flex: 1;
  display: flex;
  align-items: flex-end;
  gap: 0.35rem;
  min-width: 0;
}

.mention-wrapper {
  flex: 1;
  position: relative;
  min-width: 0;
}

.comment-input {
  width: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  padding: 0.4rem 0.85rem;
  font-size: 0.78rem;
  color: #2d3748;
  resize: none;
  outline: none;
  max-height: 110px;
  overflow-y: auto;
  background: #f8fafc;
  line-height: 1.45;
  transition: border-color 0.15s, background 0.15s;
}

.comment-input:focus {
  border-color: #667eea;
  background: white;
}

.comment-input::placeholder { color: #cbd5e0; }

.send-btn {
  background: none;
  border: none;
  padding: 0.2rem 0.3rem;
  color: #667eea;
  font-size: 0.95rem;
  cursor: pointer;
  flex-shrink: 0;
  opacity: 0.7;
  transition: opacity 0.15s;
  line-height: 1;
}

.send-btn:disabled {
  opacity: 0.25;
  cursor: default;
}

.send-btn:not(:disabled):hover { opacity: 1; }

/* ── Mention dropdown ── */
.mention-dropdown {
  position: absolute;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 180px;
  overflow-y: auto;
}

.mention-dropdown--new {
  bottom: calc(100% + 6px);
}

.mention-dropdown--edit {
  top: calc(100% + 4px);
}

.mention-loading-row {
  display: flex;
  justify-content: center;
  padding: 0.6rem;
}

.mention-item {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  width: 100%;
  background: none;
  border: none;
  padding: 0.45rem 0.65rem;
  text-align: left;
  cursor: pointer;
  font-size: 0.78rem;
  color: #2d3748;
  transition: background 0.1s;
}

.mention-item:hover,
.mention-item--active {
  background: #eef2ff;
}

.mention-item-avatar {
  flex-shrink: 0;
}

.mention-item-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mention-empty {
  font-size: 0.72rem;
  color: #a0aec0;
  padding: 0.5rem 0.65rem;
  text-align: center;
}

/* Mention highlight in rendered comments */
.comment-text >>> .mention-tag {
  color: #667eea;
  font-weight: 600;
}
</style>



// WEBPACK FOOTER //
// src/app/news_feed/components/PostComments.vue